import { retrieveStoredItem } from 'app/tools/hooks/local-storage/local-storage-methods';
import { LSKey } from 'app/tools/hooks/local-storage/local-storage.types';
import { lazyOf } from 'app/tools/utils';
import Axios from 'axios-observable';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { JsonObject } from 'type-fest';
import { FormDataSource, MappedApi, RawApi } from './api-core.types';
import {
  ConfigOptions,
  getConfig,
  manageAxiosResponse,
  mapRequestData,
} from './api-request-utils';

//
// ---------- INTERNAL REQUEST FUNCTIONS ------------

type Body = JsonObject | FormDataSource;
type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
type BodyMethod = Extract<Method, 'POST' | 'PATCH' | 'PUT'>;
type NonBodyMethod = Exclude<Method, BodyMethod>;

type ApiRequest = {
  method: Method;
  url: string;
  body?: Body;
  options?: ConfigOptions;
  notAppendSlash?: boolean;
};
const apiRequest = <T>({
  method,
  url,
  body,
  options = {},
  notAppendSlash,
}: ApiRequest): Observable<MappedApi<T>> =>
  lazyOf(() => getConfig(options)).pipe(
    switchMap(config => {
      let modifiedUrl = url;
      if (notAppendSlash !== true) {
        modifiedUrl = url.slice(-1) === '/' ? url : `${url}/`; // append slash
      }
      if (retrieveStoredItem(LSKey.DEV_TESTING_MODE)) {
        modifiedUrl = modifiedUrl.replace(
          'backend-prod-dot',
          'backend-dev-dot'
        );
      }
      return Axios.request<RawApi<T>>({
        url: modifiedUrl,
        data: body && mapRequestData(body, options),
        method,
        ...config,
      });
    }),
    manageAxiosResponse()
  );

const requestWithBodyFactory =
  (method: BodyMethod) =>
  <T>(url: string, body?: Body, options?: ConfigOptions) =>
    apiRequest<T>({ url, method, body, options });

const requestWithoutBodyFactory =
  (method: NonBodyMethod) =>
  <T>(
    url: string,
    options?: ConfigOptions,
    notAppendSlash?: boolean
  ) =>
    apiRequest<T>({ url, method, options, notAppendSlash });

//
// ------------------ EXPORTS --------------------

export const apiGet = requestWithoutBodyFactory('GET');
export const apiPost = requestWithBodyFactory('POST');
export const apiPut = requestWithBodyFactory('PUT');
export const apiPatch = requestWithBodyFactory('PATCH');
export const apiDelete = requestWithoutBodyFactory('DELETE');
